import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { 
  selectSelectedSite, 
  selectSelectedSiteId, 
  selectLocations, 
  selectSelectedLocationId,
  selectSelectedLocation,
  selectSelectedGDM
} from '@/store/selectors';
import { useAppSelector } from '@/store/hooks';
import type { RootState } from '@/store/store';
import MiniLoader from '../MiniLoader';
import type { GridColDef } from '@mui/x-data-grid';
import FieldLabel from '../common/FieldLabel';
import DeviceService from '../../services/devices';
import WalkdownService from '../../services/walkdowns';
import ProductService from '../../services/product';
import CommonDataGrid from '../common/CommonDataGrid';
import type {
  WalkdownDeviceType as DeviceType,
  WalkdownDeviceTypeCategory as DeviceTypeCategory,
  WalkdownSearchResultDevice as SearchResultDevice,
  WalkdownBaseModel as BaseModel,
  WalkdownLinkDevicePopupProps
} from '@/types/walkdown';

const WalkdownLinkDevicePopup: React.FC<WalkdownLinkDevicePopupProps> = ({
  open,
  onClose,
  currentDevice = null,
  onSuccess,
  isLinked = true,
}) => {
  const { t } = useTranslation();
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResultDevice[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [linkingDeviceId, setLinkingDeviceId] = useState<string | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advDeviceTypeCategory, setAdvDeviceTypeCategory] = useState<string>('');
  const [advDeviceType, setAdvDeviceType] = useState<string>('');
  const [advBaseModel, setAdvBaseModel] = useState<string>('');
  const [deviceTypeChartData, setDeviceTypeChartData] = useState<DeviceTypeCategory[]>([]);
  const [availableDeviceTypes, setAvailableDeviceTypes] = useState<DeviceType[]>([]);
  const [loadingChartData, setLoadingChartData] = useState(false);
  const [allBaseModels, setAllBaseModels] = useState<BaseModel[]>([]);
  const [availableBaseModels, setAvailableBaseModels] = useState<BaseModel[]>([]);
  const [loadingBaseModels, setLoadingBaseModels] = useState(false);  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);  const [totalCount, setTotalCount] = useState(0);  const [hasSearched, setHasSearched] = useState(false);
  
  // Get site details using selector
  const siteDetails = useAppSelector(selectSelectedSite);
  const selectedGDM = useAppSelector(selectSelectedGDM);
  const locationDetails = useAppSelector(selectSelectedLocation);
  
  // Clear search results when serialNumber is cleared
  React.useEffect(() => {
    if (!serialNumber.trim() && !showAdvancedSearch) {
      setSearchResults([]);
      setSearchError(null);
      setTotalCount(0);
    }
  }, [serialNumber, showAdvancedSearch]);
  React.useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setSuccessMessage(null);
      setShowAdvancedSearch(false);
      
      // Only set serial number in normal search mode, not in advanced search
      if (currentDevice?.serialNumber && !showAdvancedSearch) {
        setSerialNumber(currentDevice.serialNumber);
      }
      
      fetchDeviceTypeChartData();
      fetchBaseModels();
    } else {
      clearSearchData();
    }
  }, [open, currentDevice]);

  const clearSearchData = () => {
    setSerialNumber('');
    setSearchResults([]);
    setSearchError(null);
    setAdvDeviceTypeCategory('');
    setAdvDeviceType('');
    setAdvBaseModel('');
    setPage(0);
    setTotalCount(0);
    setHasSearched(false);
  };
  const fetchBaseModels = async () => {
    setLoadingBaseModels(true);
    try {
      const productService = new ProductService();
      const response = await productService.getBaseModels();
      
      if (response?.objects) {
        setAllBaseModels(response.objects);
      }
    } catch (error) {
      console.error('Error fetching base models:', error);
    } finally {
      setLoadingBaseModels(false);
    }
  };  const fetchDeviceTypeChartData = async () => {
    if (!selectedGDM) {
      console.warn('No GDM number available for fetching device type chart data');
      return;
    }
    
    setLoadingChartData(true);
    try {
      const deviceService = new DeviceService();
      const params = {
        requestType: 'device_type',
        gdmNumber: selectedGDM,
        location_id: locationDetails?.id ? String(locationDetails.id) : undefined
      };
      const response = await deviceService.getChartData(params);

      if (response?.device_type_chart_data) {
        setDeviceTypeChartData(response.device_type_chart_data);
      }
    } catch (error) {
      console.error('Error fetching device type chart data:', error);
    } finally {
      setLoadingChartData(false);
    }
  };

  React.useEffect(() => {
    if (advDeviceTypeCategory) {
      const selectedCategory = deviceTypeChartData.find(
        (category) => category.name === advDeviceTypeCategory
      );
      
      if (selectedCategory && selectedCategory.device_types) {
        setAvailableDeviceTypes(selectedCategory.device_types);
      } else {
        setAvailableDeviceTypes([]);
      }
      setAdvDeviceType('');
      setPage(0);
      setSearchResults([]);
      setSearchError(null);
      setTotalCount(0);
    } else {
      setAvailableDeviceTypes([]);
      setAdvDeviceType('');
      setSearchResults([]);
      setSearchError(null);
      setTotalCount(0);
    }
    setAdvBaseModel('');
  }, [advDeviceTypeCategory, deviceTypeChartData]);

  React.useEffect(() => {
    if (advDeviceTypeCategory && advDeviceType && allBaseModels.length > 0) {
      const filteredModels = allBaseModels.filter((model) => {
        const matchesCategory = model.device_type?.category_name === advDeviceTypeCategory;
        const matchesDeviceType = model.device_type?.device_name === advDeviceType;
        return matchesCategory && matchesDeviceType;
      });
      
      const uniqueModels = filteredModels.reduce((acc: any[], current) => {
        const exists = acc.find(item => item.base_model_code === current.base_model_code);
        if (!exists) {
          acc.push(current);
        }
        return acc;
      }, []);
      
      uniqueModels.sort((a, b) => a.base_model_code.localeCompare(b.base_model_code));
      setAvailableBaseModels(uniqueModels);
      setPage(0);
    } else {
      setAvailableBaseModels([]);
    }
    setAdvBaseModel('');
  }, [advDeviceTypeCategory, advDeviceType, allBaseModels]);

  React.useEffect(() => {
    if (showAdvancedSearch && !advDeviceType) {
      setSearchResults([]);
      setSearchError(null);
      setTotalCount(0);
      setHasSearched(false);
    }
  }, [advDeviceType, showAdvancedSearch]);

  React.useEffect(() => {
    if (hasSearched && showAdvancedSearch) {
      handleAdvancedSearch();
    }
  }, [page, pageSize]);  const handleSearch = async () => {
    if (!serialNumber.trim()) return;
    
    if (!selectedGDM) {
      setSearchError(t('siteInfoNotAvailable', 'Site information is not available. Please select a valid site.'));
      return;
    }
      setIsLoading(true);
    setSearchError(null);
    setSearchResults([]);
    
    try {
      const deviceService = new DeviceService();
      
      const filterParams: any = {
        cdm_number: selectedGDM,
      };
      
      if (locationDetails?.id) {
        filterParams.location_id = locationDetails.id;
      }      
      filterParams.search = serialNumber.trim();
        const response = await deviceService.getList({
        filter: filterParams,
        page: 1,
        size: 100
      });
      
      const devices = (response?.results || []).map((device: any) => ({
        id: device.id,
        guid: device.id,
        serialNumber: device.serial_number || '-',
        baseModel: device.base_model || '-',
        deviceType: device.device_type || '-',
        manufacturer: device.manufacturer_name || device.manufacturer || '',
        brand: device.manufacturer || '',
        category: '',
        modelString: device.base_model || '',
      }));

      setSearchResults(devices);
      setTotalCount(response?.totalCount || devices.length);
      
      if (devices.length === 0) {
        setSearchError(t('noDevicesFoundWithSerial', 'No devices found with the provided serial number.'));
      }
    } catch (error) {
      console.error('Error searching devices:', error);
      setSearchError(t('failedToSearchDevices', 'Failed to search devices. Please try again.'));
      setSearchResults([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };  const handleAdvancedSearch = async () => {
    if (!selectedGDM) {
      setSearchError(t('siteInfoNotAvailable', 'Site information is not available. Please select a valid site.'));
      return;
    }

    setIsLoading(true);
    setSearchError(null);
    setSearchResults([]);

    try {
      const deviceService = new DeviceService();
      
      const filterParams: any = {
        cdm_number: selectedGDM,
      };
      
      if (locationDetails?.id) {
        filterParams.location_id = locationDetails.id;
      }
      
      if (serialNumber.trim()) {
        filterParams.search = serialNumber.trim();
      }
      
      if (advDeviceType) {
        filterParams.device_type = advDeviceType;
      }
      
      if (advBaseModel) {
        filterParams.base_model = advBaseModel;
      }
      
      // Add device_category to filter if selected
      if (advDeviceTypeCategory) {
        filterParams.device_category = advDeviceTypeCategory;
      }
      
      // Stringify device_type as array (even if single value) before passing to service
      if (filterParams.device_type) {
        filterParams.device_type = JSON.stringify([filterParams.device_type]);
      } else {
        filterParams.device_type = JSON.stringify([]);
      }
      const response = await deviceService.getList({
        filter: filterParams,
        page: page + 1,
        size: pageSize
      });
      
      const devices = (response?.results || []).map((device: any) => ({
        id: device.id,
        guid: device.id,
        serialNumber: device.serial_number || '-',
        baseModel: device.base_model || '-',
        deviceType: device.device_type || '-',
        manufacturer: device.manufacturer_name || device.manufacturer || '',
        brand: device.manufacturer || '',
        category: device.device_category || '', // show device_category in results if needed
        modelString: device.base_model || '',
      }));

      setSearchResults(devices);
      setTotalCount(response?.totalCount || 0);

      if (devices.length === 0) {
        setSearchError(t('noDevicesFoundWithCriteria', 'No devices found with the provided criteria.'));
      }
    } catch (error) {
      console.error('Error searching devices:', error);
      setSearchError(t('failedToSearchDevices', 'Failed to search devices. Please try again.'));
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };  const columns: GridColDef[] = [
    {
      field: 'serialNumber',
      headerName: t('serialNumber', 'Serial Number'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'baseModel',
      headerName: t('baseModel', 'Base Model'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'deviceType',
      headerName: t('deviceType', 'Device Type'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: 200,
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const isLinking = linkingDeviceId === params.row.id;
        return (
          <Button
            variant="contained"
            onClick={() => handleLinkDevice(params.row.id)}
            disabled={isLinking || !!linkingDeviceId}
            sx={{ height: '40px', whiteSpace: 'nowrap' }}
          >
            {isLinking ? (
              <>
                <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                {t('linking', 'Linking...')}
              </>
            ) : (
              t('linkThisDevice', 'LINK THIS DEVICE')
            )}
          </Button>
        );
      },
    }
  ];
  const handleLinkDevice = async (deviceId: string) => {

    // Validate required data (userScope is NOT required for link API)
    if (!currentDevice) {
      const msg = t('unableToLinkNoDevice', 'Unable to link device. No walkdown device selected (currentDevice is null).');
      console.error(msg);
      setErrorMessage(msg);
      return;
    }
    
    if (!currentDevice.id) {
      const msg = t('unableToLinkNoId', 'Unable to link device. Walkdown device ID is missing.');
      console.error(msg, currentDevice);
      setErrorMessage(msg);
      return;
    }

    const selectedDevice = (searchResults || []).find(d => d.id === deviceId);    
    if (!selectedDevice) {
      setErrorMessage(t('selectedDeviceNotFound', 'Selected device not found.'));
      return;
    }

    setLinkingDeviceId(deviceId);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const walkdownService = new WalkdownService();
      
      console.log('Calling linkDevice API with:', {
        device_guid: selectedDevice.guid || selectedDevice.id,
        walkdown_device_id: currentDevice.id
      });
      
      await walkdownService.linkDevice(
        selectedDevice.guid || selectedDevice.id,
        currentDevice.id
      );
      
      setSuccessMessage(t('deviceLinkedSuccessfully', 'Device linked successfully!'));
      setSearchResults([]);
      setSerialNumber('');
      
      if (onSuccess) {
        onSuccess('link');
      }
    } catch (error: any) {
      console.error('Error linking device:', error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.detail || error.message || t('failedToLinkDevice', 'Failed to link device. Please try again.');
      setErrorMessage(errorMessage);
    } finally {
      setLinkingDeviceId(null);
    }
  };
  const handleUnlinkDevice = async () => {
    if (!currentDevice?.id) {
      setErrorMessage(t('unableToUnlinkMissingInfo', 'Unable to unlink device. Missing required information.'));
      return;
    }

    setUnlinking(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const walkdownService = new WalkdownService();
      
      await walkdownService.unlinkDevice(currentDevice.id);

      if (onSuccess) {
        onSuccess('unlink');
      }
    } catch (error) {
      console.error('Error unlinking device:', error);
      setErrorMessage(t('failedToUnlinkDevice', 'Failed to unlink device. Please try again.'));
    } finally {
      setUnlinking(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 600, fontSize: "1rem", background: "#F8F9FA", padding: "20px 32px", borderBottom: "1px solid #e9ecef" }}>
        {t('linkDevice', 'LINK DEVICE')}
        <IconButton onClick={onClose} aria-label="Close" sx={{ color: "#666", padding: "4px" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ padding: "32px" }}>
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <Box mb={2}>
          {currentDevice?.deviceId && (
            <Typography
              variant="body1"
              component="div"
              onClick={handleUnlinkDevice}
              sx={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#00573d',
                mb: 2,
                cursor: unlinking ? 'not-allowed' : 'pointer',
                opacity: unlinking ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              {unlinking ? (
                <>
                  <CircularProgress size={20} sx={{ color: '#00573d' }} />
                  {t('unlinkingDevice', 'Unlinking Device...')}
                </>
              ) : (
                t('removeCurrentDeviceLink', 'REMOVE CURRENT DEVICE LINK')
              )}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mb: 2, color: "#262728" }}>
            {isLinked 
              ? t('useThisForm', 'Use this form to link a different device.')
              : t('linkNewDevice', 'Use this form to link a device.')
            }
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#666', mb: 2 }}>
            {t('deviceLinkNote', 'Only stand-alone devices are available for linking. To link a device to another asset, the component should be unlinked from the current asset first.')}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {!showAdvancedSearch && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('introduceSerialNumber', 'Please introduce the serial number to search for devices:')}
            </Typography>            
            <Grid container spacing={2} mt={2} mb={3}>
              <Grid size={{ xs: 12, sm: 9 }}>
                <FieldLabel label={t('serialNumber', 'SERIAL NUMBER')} />
                <TextField
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  fullWidth
                  variant="outlined"
                  placeholder={t('enterSerialNumberPlaceholder', 'Enter Serial Number')}
                  className="link-device-input"
                  size="medium"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSearch}
                  sx={{ height: "52px", mt: '23px' }}
                >
                  {t('searchDevices', 'SEARCH DEVICES')}
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        <Box mb={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                color: '#262728',
                cursor: 'pointer',
                textDecoration: showAdvancedSearch ? 'underline' : 'none',
                textUnderlineOffset: '3px',
                mb: 0,
                '&:hover': { textDecoration: 'underline' },
              }}
              onClick={() => {
                if (showAdvancedSearch) {
                  clearSearchData();
                  // When switching back to normal search, restore serial number if available
                  if (currentDevice?.serialNumber) {
                    setSerialNumber(currentDevice.serialNumber);
                  }
                } else {
                  // When switching to advanced search, clear the serial number
                  setSerialNumber('');
                  setSearchResults([]);
                  setSearchError(null);
                }
                setShowAdvancedSearch(!showAdvancedSearch);
              }}
            >
              {t('advancedSearch', 'ADVANCED SEARCH')}
            </Typography>
            <IconButton
              size="small"
              sx={{ p: 0, verticalAlign: 'middle' }}
              onClick={() => {
                if (showAdvancedSearch) {
                  clearSearchData();
                  // When switching back to normal search, restore serial number if available
                  if (currentDevice?.serialNumber) {
                    setSerialNumber(currentDevice.serialNumber);
                  }
                } else {
                  // When switching to advanced search, clear the serial number
                  setSerialNumber('');
                  setSearchResults([]);
                  setSearchError(null);
                }
                setShowAdvancedSearch(!showAdvancedSearch);
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d={showAdvancedSearch ? 'M7 14l5-5 5 5' : 'M7 10l5 5 5-5'} stroke="#262728" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </IconButton>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {showAdvancedSearch && (          
          <Grid container spacing={2} mt={2} mb={3}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel label="DEVICE TYPE CATEGORY" />
              <Autocomplete
                value={deviceTypeChartData.find(c => c.name === advDeviceTypeCategory) || null}
                onChange={(_, newValue) => {
                  setAdvDeviceTypeCategory(newValue?.name || '');
                }}
                options={deviceTypeChartData}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t('selectDeviceTypeCategoryPlaceholder', 'Select Device Type Category')}
                    className="link-device-input"
                    size="medium"
                  />
                )}
                size="medium"
                fullWidth
                disabled={loadingChartData}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel label="DEVICE TYPE" />
              <Autocomplete
                value={availableDeviceTypes.find(d => d.name === advDeviceType) || null}
                onChange={(_, newValue) => {
                  setAdvDeviceType(newValue?.name || '');
                }}
                options={availableDeviceTypes}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.name === value.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t('selectDeviceTypePlaceholder', 'Select Device Type')}
                    className="link-device-input"
                    size="medium"
                  />
                )}
                size="medium"
                fullWidth
                disabled={!advDeviceTypeCategory || availableDeviceTypes.length === 0}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FieldLabel label={t('baseModel', 'BASE MODEL')} />
              <Autocomplete
                value={availableBaseModels.find(m => m.base_model_code === advBaseModel) || null}
                onChange={(_, newValue) => {
                  setAdvBaseModel(newValue?.base_model_code || '');
                }}
                options={availableBaseModels}
                getOptionLabel={(option) => option.base_model_code}
                isOptionEqualToValue={(option, value) => option.base_model_code === value.base_model_code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder={t('selectBaseModel', 'Select Base Model')}
                    className="link-device-input"
                    size="medium"
                  />
                )}
                size="medium"
                fullWidth
                disabled={!advDeviceType || availableBaseModels.length === 0 || loadingBaseModels}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <FieldLabel label={t('serialNumber', 'SERIAL NUMBER')} />
              <TextField
                variant="outlined"
                value={serialNumber}
                className="link-device-input"
                onChange={e => setSerialNumber(e.target.value)}
                size="medium"
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  setPage(0);
                  setHasSearched(true);
                  handleAdvancedSearch();
                }}
                // Enable button if any of the advanced search fields are filled
                disabled={
                  !advDeviceTypeCategory &&
                  !advDeviceType &&
                  !advBaseModel &&
                  !serialNumber.trim()
                }
                sx={{
                  backgroundColor: "#00573d",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: 0,
                  textTransform: "uppercase",
                  height: "52px",
                  mt: '23px'
                }}
              >
                {t('searchDevices', 'SEARCH DEVICES')}
              </Button>
            </Grid>
          </Grid>
        )}

        {!isLoading && !searchError && searchResults.length === 0 && !serialNumber.trim() && (
          <Typography variant="body1">
            {t('enterSearchCriteria', 'Enter search criteria to populate list of options.')}
          </Typography>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <MiniLoader />
          </Box>
        ) : searchError ? (
          <Box mt={2}>
            <Typography variant="body1" sx={{ color: '#d32f2f', textAlign: 'center', padding: 2 }}>
              {searchError}
            </Typography>
          </Box>
        ) : searchResults.length > 0 ? (
          <Box mt={4}>
            <Typography variant="h4" fontWeight={400} sx={{ mb: 2 }}>
              {t('matchingDevices', 'Matching Device(s)')}
            </Typography>            
            <Box sx={{ border: "0px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }}>
              <CommonDataGrid
                rows={searchResults}
                columns={columns}
                pageSizeOptions={[25, 50, 100]}
                paginationModel={{ page, pageSize }}
                totalRowCount={totalCount}
                paginationMode="server"
                onPaginationModelChange={(model) => {
                  setPage(model.page);
                  setPageSize(model.pageSize);
                }}
                loading={isLoading}                error={searchError}
                noRowsMessage={t('noDevicesFound', 'No devices found.')}
                pageName="Devices"
              />
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default WalkdownLinkDevicePopup;
